import React, { useMemo } from 'react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import { MailingClassStep } from '@/components/postgrid/mailing-class';
import {
  AttachmentSelectionStep,
  ChequeReviewStep,
  Wizard,
  type WizardStepDefinition,
} from '@/components/postgrid/wizard';
import type { CreatePostgridCheque } from '@/services/api/generated/postgridService';

import { ChequeRecipientAmountStep } from './ChequeRecipientAmountStep';

interface ChequeWizardProps {
  matterId: string;
  onSuccess: (data: CreatePostgridCheque) => void;
  onCancel?: () => void;
  isLoading?: boolean;
  // Prefill options for better UX
  prefilledToContactId?: string;
  prefilledAmount?: string | number;
  prefilledMemo?: string;
}

const chequeWizardSteps: WizardStepDefinition[] = [
  {
    id: 'recipient-amount',
    title: 'Recipient & Amount',
    description: 'Select recipient & enter amount',
  },
  {
    id: 'attachments',
    title: 'Attachments',
    description: 'Include supporting documents',
  },
  {
    id: 'mailing-class',
    title: 'Mailing Class',
    description: 'Delivery speed & extras',
  },
  {
    id: 'review',
    title: 'Review',
    description: 'Confirm & submit',
  },
];

export const ChequeWizard: React.FC<ChequeWizardProps> = ({
  matterId,
  onSuccess,
  onCancel,
  isLoading = false,
  prefilledToContactId,
  prefilledAmount,
  prefilledMemo,
}) => {
  // Generate a stable idempotency key for this wizard session
  const idempotencyKey = useMemo(
    () => `cheque-${matterId}-${uuidv4()}`,
    [matterId],
  );

  const handleComplete = async (wizardData: Record<string, any>) => {
    try {
      const payload: CreatePostgridCheque = {
        amount: wizardData.amount,
        memo: wizardData.memo || null,
        to_contact_id: wizardData.to_contact_id,
        from_contact_id: '', // No sender field in UI - backend will handle default
        matter_id: matterId,
        // Backend will handle these defaults
        cheque_type: 'standard',
        cheque_size: 'us_letter',
        mailing_class: wizardData.mailing_class,
        bank_account_id: '', // Backend will use default
        return_envelope_id: null,
        use_return_envelope: wizardData.use_return_envelope,
        // Use stable idempotency key for this wizard session
        idempotency_key: idempotencyKey,
        blob_ids: wizardData.attachment_blob_ids ?? [],
      };

      onSuccess(payload);
      toast.success('Cheque created successfully!');
    } catch (error) {
      console.error('Failed to create cheque:', error);
      toast.error('Failed to create cheque. Please try again.');
    }
  };

  // Prepare initial data with prefilled values
  const initialData = {
    ...(prefilledAmount && { amount: prefilledAmount.toString() }),
    ...(prefilledMemo && { memo: prefilledMemo }),
    ...(prefilledToContactId && { to_contact_id: prefilledToContactId }),
    use_return_envelope: false, // Initialize with default value
  };

  return (
    <Wizard
      title="Send a Check"
      description="Follow these steps to create and send a professional cheque."
      steps={chequeWizardSteps}
      onComplete={handleComplete}
      onCancel={onCancel}
      isLoading={isLoading}
      initialData={initialData}
    >
      <ChequeRecipientAmountStep />
      <AttachmentSelectionStep matterId={matterId} />
      <MailingClassStep />
      <ChequeReviewStep matterId={matterId} />
    </Wizard>
  );
};
